import React, { ChangeEvent, useCallback, useRef } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import Row from 'components/Common/Row';
import CloseButton from 'components/CloseButton';

const FileRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  border: 1px solid var(--color-border-primary);
  border-radius: 8px;
  margin-bottom: 6px;
  background: var(--color-bg-primary);
`;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const AttachmentsPicker = ({
  files,
  setFiles,
}: {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onFilesAdded = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const picked = event.target.files;
      if (!picked || picked.length === 0) {
        return;
      }
      const next = Array.from(picked);
      setFiles((current) => [...current, ...next]);
      // Reset the input so picking the same file again still fires onChange.
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [setFiles],
  );

  const onRemove = useCallback(
    (index: number) => {
      setFiles((current) => current.filter((_, i) => i !== index));
    },
    [setFiles],
  );

  const onAddClicked = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }, []);

  return (
    <div>
      {files.map((file, index) => (
        <FileRow key={`${file.name}-${index}`}>
          <Row centerAlign style={{ gap: 8, minWidth: 0 }}>
            <Typography.Body type="Label" isEllipsis>
              {file.name}
            </Typography.Body>
            <Typography.Body type="Body 12" color="var(--color-text-muted)">
              {formatBytes(file.size)}
            </Typography.Body>
          </Row>
          <CloseButton close={() => onRemove(index)} />
        </FileRow>
      ))}
      <Button
        type="Tertiary"
        size="Small"
        icon="add"
        label={files.length === 0 ? 'Add image' : 'Add another'}
        onClick={onAddClicked}
        testId="add-attachment"
      />
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={onFilesAdded}
        data-testid="attachments-input"
      />
    </div>
  );
};

export default AttachmentsPicker;
